export type Rgb = {
    r: number
    g: number
    b: number
}

type Hsl = {
    h: number
    s: number
    l: number
}

const clamp = (value: number, min: number, max: number): number => Math.min(max, Math.max(min, value))

export const hexToRgb = (hex: string): Rgb => {
    const clean = hex.replace('#', '').trim()
    const full = clean.length === 3 ? clean.split('').map(char => char + char).join('') : clean.padEnd(6, '0').slice(0, 6)
    const value = parseInt(full, 16)

    if (Number.isNaN(value)) return { r: 0, g: 0, b: 0 }

    return { r: (value >> 16) & 255, g: (value >> 8) & 255, b: value & 255 }
}

export const rgbToHex = ({ r, g, b }: Rgb): string => {
    return '#' + [r, g, b].map(channel => Math.round(clamp(channel, 0, 255)).toString(16).padStart(2, '0')).join('')
}

const linear = (channel: number): number => {
    const value = channel / 255
    return value <= 0.04045 ? value / 12.92 : Math.pow((value + 0.055) / 1.055, 2.4)
}

export const luminance = (hex: string): number => {
    const { r, g, b } = hexToRgb(hex)
    return 0.2126 * linear(r) + 0.7152 * linear(g) + 0.0722 * linear(b)
}

/** WCAG contrast ratio between two colours, from 1 to 21. */
export const contrast = (a: string, b: string): number => {
    const first = luminance(a)
    const second = luminance(b)
    const high = Math.max(first, second)
    const low = Math.min(first, second)
    return (high + 0.05) / (low + 0.05)
}

export const mix = (from: string, to: string, amount: number): string => {
    const a = hexToRgb(from)
    const b = hexToRgb(to)
    const t = clamp(amount, 0, 1)

    return rgbToHex({
        r: a.r + (b.r - a.r) * t,
        g: a.g + (b.g - a.g) * t,
        b: a.b + (b.b - a.b) * t
    })
}

export const withAlpha = (hex: string, alpha: number): string => {
    const { r, g, b } = hexToRgb(hex)
    return `rgba(${r}, ${g}, ${b}, ${clamp(alpha, 0, 1)})`
}

const rgbToHsl = ({ r, g, b }: Rgb): Hsl => {
    const red = r / 255
    const green = g / 255
    const blue = b / 255
    const max = Math.max(red, green, blue)
    const min = Math.min(red, green, blue)
    const l = (max + min) / 2

    if (max === min) return { h: 0, s: 0, l }

    const d = max - min
    const s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
    let h = 0

    if (max === red) h = (green - blue) / d + (green < blue ? 6 : 0)
    else if (max === green) h = (blue - red) / d + 2
    else h = (red - green) / d + 4

    return { h: h * 60, s, l }
}

const hslToRgb = ({ h, s, l }: Hsl): Rgb => {
    const c = (1 - Math.abs(2 * l - 1)) * s
    const x = c * (1 - Math.abs(((h / 60) % 2) - 1))
    const m = l - c / 2
    let rgb: [number, number, number] = [0, 0, 0]

    if (h < 60) rgb = [c, x, 0]
    else if (h < 120) rgb = [x, c, 0]
    else if (h < 180) rgb = [0, c, x]
    else if (h < 240) rgb = [0, x, c]
    else if (h < 300) rgb = [x, 0, c]
    else rgb = [c, 0, x]

    return { r: (rgb[0] + m) * 255, g: (rgb[1] + m) * 255, b: (rgb[2] + m) * 255 }
}

/** Moves a colour's lightness, keeping its hue, until it reaches the contrast asked for. */
export const ensureContrast = (color: string, against: string, minimum: number): { color: string, adjusted: boolean } => {
    if (contrast(color, against) >= minimum) return { color, adjusted: false }

    const hsl = rgbToHsl(hexToRgb(color))
    const lighten = luminance(against) < 0.4
    let l = hsl.l

    for (let step = 0; step < 100; step++) {
        l = lighten ? Math.min(1, l + 0.01) : Math.max(0, l - 0.01)
        const candidate = rgbToHex(hslToRgb({ ...hsl, l }))
        if (contrast(candidate, against) >= minimum) return { color: candidate, adjusted: true }
    }

    return { color: lighten ? '#ffffff' : '#000000', adjusted: true }
}

/** Picks whichever of two inks reads better on a background. */
export const bestInk = (background: string, dark = '#0b0b0c', light = '#ffffff'): string => {
    return contrast(dark, background) >= contrast(light, background) ? dark : light
}
